/**
 * Organization Service
 * Handles organization-related operations (details, contacts, links, plans, users)
 */

import { apiClient, handleApiError } from './apiClient';
import { USERS_API_BASE_URL, MESSAGES_API_BASE_URL } from '../constants';

/**
 * Get organization details by UUID
 * @param orgUuid - Organization UUID
 * @param authToken - Authentication token
 * @returns Promise with organization details
 * @throws ApiError if request fails
 */
export const getOrganisation = async (
  orgUuid: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation/${orgUuid}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get organization list
 * @param authToken - Authentication token
 * @returns Promise with organization list
 * @throws ApiError if request fails
 */
export const getOrganisationList = async (
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get all organization details (list endpoint)
 * @param authToken - Authentication token
 * @returns Promise with organization details list
 * @throws ApiError if request fails
 */
export const getOrganisationDetails = async (
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation/list`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get organization details by ID
 * @param authToken - Authentication token
 * @param orgUuid - Organization UUID
 * @returns Promise with organization details
 * @throws ApiError if request fails
 */
export const getOrganisationDetailsByID = async (
  authToken: string,
  orgUuid: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation/${orgUuid}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get organization contacts
 * @param authToken - Authentication token
 * @param orgId - Organization ID
 * @returns Promise with organization contacts
 * @throws ApiError if request fails
 */
export const getOrganisationContact = async (
  authToken: string,
  orgId: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation/${orgId}/org-contacts`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get organization contact detail
 * @param authToken - Authentication token
 * @param orgId - Organization ID
 * @param contactId - Contact ID
 * @returns Promise with contact details
 * @throws ApiError if request fails
 */
export const getOrganisationContactDetail = async (
  authToken: string,
  orgId: string,
  contactId: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation/${orgId}/org-contacts-detail/${contactId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get organization links
 * @param authToken - Authentication token
 * @param orgId - Organization ID
 * @returns Promise with organization links
 * @throws ApiError if request fails
 */
export const getOrganisationLink = async (
  authToken: string,
  orgId: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation/${orgId}/org-link`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get organization link detail
 * @param authToken - Authentication token
 * @param orgId - Organization ID
 * @param linkId - Link ID
 * @returns Promise with link details
 * @throws ApiError if request fails
 */
export const getOrganisationLinkDetail = async (
  authToken: string,
  orgId: string,
  linkId: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation/${orgId}/org-link-detail/${linkId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update organization address
 * @param authToken - Authentication token
 * @param orgUuid - Organization UUID
 * @param addressData - Updated address data
 * @returns Promise with update response
 * @throws ApiError if update fails
 */
export const updateOrganisationAddress = async (
  authToken: string,
  orgUuid: string,
  addressData: any
): Promise<any> => {
  try {
    const response = await apiClient.patch(
      `${USERS_API_BASE_URL}/organisation/${orgUuid}/update-org-address`,
      addressData,
      {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get organization plans
 * @param orgUuid - Organization UUID
 * @param authToken - Authentication token
 * @returns Promise with organization plans
 * @throws ApiError if request fails
 */
export const getOrganisationPlans = async (
  orgUuid: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation/${orgUuid}/plans/`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get all subscription plans (not organization-specific)
 * @param authToken - Authentication token
 * @returns Promise with all plans including feature keys
 * @throws ApiError if request fails
 */
export const getAllPlans = async (
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation/plans/all`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Assign a plan to an organization
 * Copies plan features to organization's featureskey
 * @param planId - Plan ID to assign
 * @param orgUuid - Organization UUID
 * @param authToken - Authentication token
 * @returns Promise with assignment response
 * @throws ApiError if request fails
 */
export const assignPlanToOrganization = async (
  planId: number,
  orgUuid: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${USERS_API_BASE_URL}/organisation/assign-plan`,
      {
        planId,
        orgUuid
      },
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};


/**
 * Get app users for organization
 * @param authToken - Authentication token
 * @returns Promise with app users
 * @throws ApiError if request fails
 */
export const getAppUsers = async (authToken: string): Promise<any> => {
  try {
    const response = await apiClient.get(`${USERS_API_BASE_URL}/`, {
      headers: { Authorization: authToken },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get admin app users for organization
 * @param authToken - Authentication token
 * @param userAddedBy - User added by (unused parameter for backward compatibility)
 * @param orgId - Organization ID
 * @returns Promise with admin app users
 * @throws ApiError if request fails
 */
export const getAppUsersAdmin = async (
  authToken: string,
  userAddedBy: any,
  orgId: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/admin/${orgId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get internal users (admin only)
 * @param authToken - Authentication token
 * @returns Promise with internal users
 * @throws ApiError if request fails
 */
export const getInternalUsersAdmin = async (
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/internaluser`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Fetch app users (linked users) for organization
 * @param orgId - Organization ID
 * @param authToken - Authentication token
 * @returns Promise with linked users
 * @throws ApiError if request fails
 */
export const fetchAppUsers = async (
  orgId: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${MESSAGES_API_BASE_URL}/linkeduser/${orgId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Fetch app users for direct messaging (includes existing DM group info)
 * @param orgId - Organization ID
 * @param authToken - Authentication token
 * @returns Promise with linked users enriched with existingDmGroupUuid field
 * @throws ApiError if request fails
 */
export const fetchAppUsersForDM = async (
  orgId: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${MESSAGES_API_BASE_URL}/linkeduser-for-dm/${orgId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Fetch parent users for organization
 * @param orgId - Organization ID
 * @param authToken - Authentication token
 * @returns Promise with linked parents
 * @throws ApiError if request fails
 */
export const fetchParents = async (
  orgId: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${MESSAGES_API_BASE_URL}/linkedparent/${orgId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Fetch app users by verification code
 * @param orgId - Organization ID
 * @param code - Verification code
 * @param authToken - Authentication token
 * @returns Promise with linked users by code
 * @throws ApiError if request fails
 */
export const fetchAppUsersByCode = async (
  orgId: string,
  code: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${MESSAGES_API_BASE_URL}/linkeduserbycode/${orgId}/${code}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Fetch app users by category ID
 * @param orgId - Organization ID
 * @param categoryId - Category ID (can be string or number)
 * @param authToken - Authentication token
 * @returns Promise with linked users by category
 * @throws ApiError if request fails
 */
export const fetchAppUsersByCategoryId = async (
  orgId: string,
  categoryId: string | number,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${MESSAGES_API_BASE_URL}/linkeduserbycatId/${orgId}/${categoryId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Disable parent code
 * @param postData - Disable request data
 * @param authToken - Authentication token
 * @returns Promise with disable response
 * @throws ApiError if request fails
 */
export const disableParentCode = async (
  postData: any,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${MESSAGES_API_BASE_URL}/disableparentuser`,
      postData,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Enable parent code
 * @param postData - Enable request data
 * @param authToken - Authentication token
 * @returns Promise with enable response
 * @throws ApiError if request fails
 */
export const enableParentCode = async (
  postData: any,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${MESSAGES_API_BASE_URL}/enableparentuser`,
      postData,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Disable verification code
 * @param postData - Disable request data
 * @param authToken - Authentication token
 * @returns Promise with disable response
 * @throws ApiError if request fails
 */
export const disableVerificationCode = async (
  postData: any,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${MESSAGES_API_BASE_URL}/disableverificationcode`,
      postData,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Enable verification code
 * @param postData - Enable request data
 * @param authToken - Authentication token
 * @returns Promise with enable response
 * @throws ApiError if request fails
 */
export const enableVerificationCode = async (
  postData: any,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${MESSAGES_API_BASE_URL}/enableverificationcode`,
      postData,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Check if organization is ready for notifications
 * @param authToken - Authentication token
 * @returns Promise with readiness status
 * @throws ApiError if request fails
 */
export const checkOrganisationReadiness = async (
  authToken: string
): Promise<{
  ready: boolean;
  message: string;
  missingItems: string[];
}> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/organisation/ready`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Check if customer exists in School Link database
 * @param custID - Customer ID to check
 * @param authToken - Authentication token
 * @returns Promise with existence status and organisation data if exists
 * @throws ApiError if request fails
 */
export const checkCustomerExists = async (
  custID: number,
  authToken: string
): Promise<{
  exists: boolean;
  organisation?: any;
  message: string;
}> => {
  try {
    const response = await apiClient.post(
      `${USERS_API_BASE_URL}/organisation/migrate_customer`,
      { custID },
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Fetch organisation from ePub API
 * @param custID - Customer ID to fetch
 * @param authToken - Authentication token
 * @returns Promise with ePub organisation data
 * @throws ApiError if request fails
 */
export const fetchEpubOrganisation = async (
  custID: number,
  authToken: string
): Promise<{
  data?: any;
  error?: boolean;
  message: string;
}> => {
  try {
    const response = await apiClient.post(
      `${USERS_API_BASE_URL}/organisation/migrate_customer`,
      { custID },
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return {
        error: true,
        message: error.response.data?.message || 'Record does not exist with the customer ID you provided'
      };
    }
    throw handleApiError(error);
  }
};
